import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides, insideItems } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-white" data-id="element-208" data-mp="3dq779mchykat">
      <div className="max-w-5xl mx-auto" data-id="element-209" data-mp="3dpxe5pw5nx0w">
        <div className="text-center mb-10" data-id="element-210" data-mp="38rcogkej4vqp">
          <h2 className="text-3xl md:text-4xl font-display font-bold uppercase mb-3 text-primary" data-id="element-211" data-mp="38snsmlbw05ic">
            See Inside Automotive Technology Instructor Ready
          </h2>
          <p className="text-lg text-body max-w-3xl mx-auto" data-id="element-212" data-mp="2h0q0t55owu82">
            Real pages from the system — visual system diagrams, component breakdowns, workshop
            procedures, diagnostic workflows, comparisons, lesson structures and professional
            instructor tools.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-6 md:gap-10 items-start mb-12" data-id="element-213" data-mp="22aim90iszvcn">
          <div data-id="element-214" data-mp="1xcz59suq14h0">
            <p className="text-xl font-display font-bold text-primary mb-3" data-id="element-215" data-mp="1xb5uc1d2ry37">
              Automotive Technology Instructor Ready is a complete PDF-based teaching system with{' '}
              <span className="text-secondary" data-id="element-216" data-mp="67dsvfc4v4zz">100% visual</span> core instructional material.
            </p>
            <p className="text-body leading-relaxed" data-id="element-217" data-mp="azx62b6wry49">
              Created to help Automotive Technology instructors plan less, teach with more structure
              and make complex vehicle systems easier for students to understand.
            </p>
          </div>
          <div className="bg-surface border border-primary/15 border-l-4 border-l-accent rounded-xl p-6" data-id="element-218" data-mp="3jefj6rbpi3uq">
            <p className="text-sm font-bold text-secondary uppercase tracking-wider mb-4" data-id="element-219" data-mp="3oe0lnbq34uc7">
              Inside you’ll find:
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5" data-id="element-220" data-mp="3sgx6l933l2jb">
              {insideItems.map(item => <li key={item} className="flex items-start gap-2 text-sm text-body" data-id="element-221" data-mp="13n9ukqwmqg07">
                  <CheckCircle2 size={16} className="text-secondary shrink-0 mt-0.5" data-id="element-222" data-mp="17njxfup4kqau" />
                  <span data-id="element-223" data-mp="2s64ahlysd7ni">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <p className="text-center text-primary font-bold mb-4" data-id="element-224" data-mp="2o63rdo0qs8a4">
          Swipe through real pages from Automotive Technology Instructor Ready
        </p>

        <div className="relative" data-id="element-225" data-mp="2egal92jdcld9">
          <div className="relative bg-primary border-2 border-primary rounded-xl shadow-lg shadow-primary/15 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-226" data-mp="2ef96oo359gqs">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-227" data-mp="20f7uichfimw0">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}: ${slide.title}`} data-id="element-228" data-mp="1vgwo6bqceuij">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-229" data-mp="1usugaubb063x" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/90 text-accent flex items-center justify-center hover:bg-primary transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label="View image larger" data-id="element-230" data-mp="1pthrw54saqz8">
              <Maximize2 size={18} data-id="element-231" data-mp="1b6j02920lujz" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-primary text-accent border border-accent/50 shadow-lg flex items-center justify-center hover:bg-secondary hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label="Previous image" data-id="element-232" data-mp="1p6tv7oacxvay">
            <ChevronLeft size={24} data-id="element-233" data-mp="27v8ka6146z94" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-primary text-accent border border-accent/50 shadow-lg flex items-center justify-center hover:bg-secondary hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label="Next image" data-id="element-234" data-mp="23wj09ulkmjgi">
            <ChevronRight size={24} data-id="element-235" data-mp="2tpnzpoyia34n" />
          </button>
        </div>

        <p className="text-center text-primary font-bold text-sm sm:text-base mt-6" data-id="element-236" data-mp="2tribodrsukwl">{slide.title}</p>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-237" data-mp="2yqlgbx3vap41">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary ${dotIndex === index ? 'w-8 bg-accent' : 'w-2.5 bg-primary/25 hover:bg-primary/40'}`} aria-label={`Go to image ${dotIndex + 1}: ${item.title}`} aria-current={dotIndex === index} data-id="element-238" data-mp="38meqj9u466in" data-mpi={dotIndex} />)}
        </div>

        <p className="text-center text-lg font-display font-bold text-primary mt-10" data-id="element-239" data-mp="3io8x9z521024">
          Real pages. Real teaching tools.{' '}
          <span className="text-secondary" data-id="element-240" data-mp="ecwx19hvjjr3">A system built to be used — not just read.</span>
        </p>
      </div>

      <AnimatePresence data-id="element-241" data-mp="abv04zbfjjsa">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-primary/95 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.title} onClick={() => setIsZoomed(false)} data-id="element-242" data-mp="a8z1f8rrcr2s">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label="Close enlarged image" data-id="element-243" data-mp="oyop5jxcdq5l">
              <X size={22} data-id="element-244" data-mp="17qpyjg7qsyt9" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-245" data-mp="1cp18dqy1f9bh" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}